import { RelativeMessagePosition } from '../message-position.types';
import { BaseMessageDOM } from './base-message-dom';

export class RelativeMessageDOM extends BaseMessageDOM implements MessageDOM {
  protected declare position: RelativeMessagePosition;
  private closeCleanupProcs: Function[] = [];

  public get isReady(): boolean {
    if (!this.buildDomComplete) return false;
    // Message should not be showable if its positioning target cannnot be found
    if (!this.positionTargetElement) {
      console.warn(`Target element not found: ${this.position.target_css_selector}`);
      return false;
    }

    // Determining if target element is visible (ref: https://stackoverflow.com/a/21696585)
    const targetElementStyles = window.getComputedStyle(this.positionTargetElement);
    if (targetElementStyles.position !== 'fixed') {
      // In jsdom, default value 'static' can be obtained as ''.
      if (targetElementStyles.position !== '') {
        // Element.offsetParent returns null if any of element's parents have the property 'display: none;'
        return this.positionTargetElement.offsetParent !== null;
      }
    }

    return targetElementStyles.display !== 'none';
  }

  public show(): void {
    if (this.el == null) return;

    document.body.appendChild(this.el);

    this.el.style.zIndex = this.getZIndexOfBodyLevelContextStack();
    this.el.style.position = this.hasPositionFixedElementUntilBody() ? 'fixed' : 'absolute';
    this.setRelativeMessagePosition();

    // Covers sites that uses an element other than document.body as the main scrollable element of the page by adding an eventListener to that element
    const targetScrollableParent = this.getScrollableParent();
    if (targetScrollableParent) {
      const scrollHandler = () => {
        window.requestAnimationFrame(() => {
          this.setRelativeMessagePosition();
        });
      };
      targetScrollableParent.addEventListener('scroll', scrollHandler);
      this.closeCleanupProcs.push(() => {
        targetScrollableParent.removeEventListener('scroll', scrollHandler);
      });
    }

    const resizeHandler = () => {
      window.requestAnimationFrame(() => {
        this.setRelativeMessagePosition();
      });
    };
    window.addEventListener('resize', resizeHandler);
    this.closeCleanupProcs.push(() => window.removeEventListener('resize', resizeHandler));

    // セッションの開始など、ページ表示直後に発火するイベントをトリガーとしてメッセージが表示される場合もある。
    // 画面がガチャガチャ動くのを防ぐために表示を1秒遅らせている。
    setTimeout(() => {
      this.el!.style.opacity = '1';
    }, 1000);
  }

  public close() {
    const isBeingClosed = super.close();
    if (isBeingClosed) {
      this.closeCleanupProcs.forEach((f) => f());
      this.closeCleanupProcs = [];
    }
    return isBeingClosed;
  }

  // same as EmbeddedMesasgeDOM
  private get positionTargetElement(): HTMLElement | null {
    if (!this.position.target_css_selector) return null;

    try {
      return document.querySelector(this.position.target_css_selector) as HTMLElement;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  private setTop(target: HTMLElement): void {
    if (this.el == null) return;

    this.messageOffset.x = target.offsetWidth / 2;
    this.messageOffset.y = -this.el.offsetHeight;
  }

  private setBottom(target: HTMLElement): void {
    if (this.el == null) return;

    this.messageOffset.x = target.offsetWidth / 2;
    this.messageOffset.y = target.offsetHeight;
  }

  private setRight(target: HTMLElement): void {
    if (this.el == null) return;

    this.messageOffset.x = target.offsetWidth;
    this.messageOffset.y = (target.offsetHeight - this.el.offsetHeight) / 2;
  }

  private setLeft(target: HTMLElement): void {
    if (this.el == null) return;

    this.messageOffset.x = -this.el.offsetWidth;
    this.messageOffset.y = (target.offsetHeight - this.el.offsetHeight) / 2;
  }

  private alignCenter(): void {
    if (this.el == null) return;

    this.messageOffset.x -= this.el.offsetWidth / 2;
  }

  private alignRightEdge(): void {
    if (this.el == null) return;

    this.messageOffset.x -= this.el.offsetWidth;
  }

  private setMessageOffsetFromTarget(target: HTMLElement): void {
    switch (this.position.placement) {
      case 'left_top':
        this.setTop(target);
        this.alignRightEdge();
        break;
      case 'top':
        this.setTop(target);
        this.alignCenter();
        break;
      case 'right_top':
        this.setTop(target);
        break;
      case 'left':
        this.setLeft(target);
        break;
      case 'right':
        this.setRight(target);
        break;
      case 'left_bottom':
        this.setBottom(target);
        this.alignRightEdge();
        break;
      case 'bottom':
        this.setBottom(target);
        this.alignCenter();
        break;
      case 'right_bottom':
        this.setBottom(target);
        break;
      default:
      // this.tracker._log('error', `${this.placement} is not a valid placement value`);
    }
  }

  private setTargetOffset(target: HTMLElement): void {
    if (this.el == null) return;

    const targetBoundingRect = target.getBoundingClientRect();
    this.targetOffset.x = targetBoundingRect.left;
    this.targetOffset.y = targetBoundingRect.top;

    if (this.el.style.position !== 'fixed') {
      this.targetOffset.x += window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft;
      this.targetOffset.y += window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
    }
  }

  private setRelativeMessagePosition(): void {
    const target = this.positionTargetElement;
    if (!this.el || !target) {
      // Close message if target cannot be found while message is being shown
      // 例: 相対位置メッセージを表示後、targetElementが削除され、その状態でwindowをresizeするとこのコードパスが実行される
      this.hideMessage();
      return;
    }

    this.setTargetOffset(target);
    this.setMessageOffsetFromTarget(target);

    this.el.style.setProperty('top', `${this.targetOffset.y + this.messageOffset.y + this.position.offset_top_px}px`);
    this.el.style.setProperty('left', `${this.targetOffset.x + this.messageOffset.x + this.position.offset_left_px}px`);
  }

  // z-idex が auto の場合は親ボックスと同じ重ね合わせレベルを持つため、メッセージ挿入位置の要素から body まで調べる
  private getZIndexOfBodyLevelContextStack(
    el: HTMLElement | null = this.positionTargetElement,
    highestLevelZIndex: string = 'auto'
  ): string {
    if (!el || el === document.body) return highestLevelZIndex;
    const zIndexOfCurrentElement = window.getComputedStyle(el).zIndex || 'auto';
    if (zIndexOfCurrentElement !== 'auto') {
      return this.getZIndexOfBodyLevelContextStack(el.parentElement, zIndexOfCurrentElement);
    }
    return this.getZIndexOfBodyLevelContextStack(el.parentElement, highestLevelZIndex);
  }

  private hasPositionFixedElementUntilBody(el: HTMLElement | null = this.positionTargetElement): boolean {
    if (!el || el === document.body) return false;
    if (window.getComputedStyle(el).position === 'fixed') return true;
    return this.hasPositionFixedElementUntilBody(el.parentElement);
  }

  // Returns the first scrollable parent of initial element
  private getScrollableParent(el: HTMLElement | null = this.positionTargetElement): HTMLElement | null {
    if (!el) return null;

    const parentEl = el.parentElement;
    if (!parentEl || parentEl === document.body) return null;

    const { overflow } = window.getComputedStyle(parentEl);
    if (overflow && overflow.includes('scroll')) return parentEl;

    return this.getScrollableParent(parentEl);
  }
}
